import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SocialMediaModal from './components/SocialMediaModal';
import Img from '../../../Img';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import { PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import { SOCIAL_MEDIA_ENDPOINT, SOCIAL_PLATFORMS } from './constants';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.social';

const buildHandles = (data) => Object.fromEntries(
    SOCIAL_PLATFORMS.map((platform) => {
        const entry = data?.[platform.key];
        return [platform.key, { handle: entry?.handle || null, url: entry?.url || null }];
    }),
);

const CollegeSocial = () => {
    const profileData = useSelector((state) => state.profile?.data) || {};
    const authUser = useSelector((state) => state.auth?.user);
    const isOwnProfile = !!authUser && !!profileData.memberId && authUser.id === profileData.memberId;
    const dispatch = useDispatch();
    const { hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();

    const [handles, setHandles] = useState(() => buildHandles());
    const [isEditing, setIsEditing] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!isOwnProfile || !hasAnnualPower) {
            return;
        }
        const fetchSocialMedia = async () => {
            try {
                const response = await getApiAxiosInstance().get(SOCIAL_MEDIA_ENDPOINT);
                setHandles(buildHandles(response.data));
            } catch (err) {
                dispatch(showErrorPopup({
                    message: 'We couldn\'t load your social handles. Please try again.',
                    error: err,
                }));
            }
        };
        fetchSocialMedia();
    }, [isOwnProfile, hasAnnualPower, dispatch, refreshKey]);

    const handleEditClick = () => {
        if (hasAnnualPower) {
            setIsEditing(true);
            return;
        }
        openUpgradePopup(PowerFunnelGatedFeatures.CPP_SOCIAL_HANDLES_LOCKED);
    };

    const handleLockKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEditClick();
        }
    };

    const handleSave = async (updatedHandles) => {
        try {
            await getApiAxiosInstance().put(SOCIAL_MEDIA_ENDPOINT, updatedHandles);
            setIsEditing(false);
            setRefreshKey((key) => key + 1);
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t save your social handles. Please try again.',
                error: err,
            }));
        }
    };

    const renderHandleValue = (platform) => {
        if (!hasAnnualPower) {
            return (
                <span
                    className={styles.socialLockBox}
                    role="button"
                    tabIndex={0}
                    onClick={handleEditClick}
                    onKeyDown={handleLockKeyDown}
                    data-testid={`${TESTID_PREFIX}.row-${platform.key}.lock`}
                >
                    <Img
                        src="/img/v4/lock.svg"
                        alt="Locked"
                        className={styles.socialLockIcon}
                    />
                </span>
            );
        }
        const entry = handles[platform.key];
        if (entry.handle) {
            return (
                <a
                    className={styles.socialHandle}
                    href={entry.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${TESTID_PREFIX}.row-${platform.key}.handle`}
                >
                    {entry.handle}
                </a>
            );
        }
        return (
            <span
                className={styles.socialPlaceholder}
                data-testid={`${TESTID_PREFIX}.row-${platform.key}.placeholder`}
            >
                {`Add ${platform.label} handle`}
            </span>
        );
    };

    return (
        <div
            className={styles.academicsSection}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div className={styles.academicsHeader}>
                <div className={styles.academicsTitleBlock}>
                    <div
                        className={styles.academicsTitle}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        Social
                    </div>
                    <div
                        className={styles.academicsSubtitle}
                        data-testid={`${TESTID_PREFIX}.subtitle`}
                    >
                        Viewable by college coaches.
                    </div>
                </div>
                {isOwnProfile && (
                    <button
                        type="button"
                        className={styles.academicsEditBtn}
                        onClick={handleEditClick}
                        data-testid={`${TESTID_PREFIX}.edit-btn`}
                    >
                        <Img
                            src="/img/v4/pen.svg"
                            alt=""
                            className={styles.academicsEditIcon}
                        />
                        <span data-testid={`${TESTID_PREFIX}.edit-btn.text`}>Edit</span>
                    </button>
                )}
            </div>

            <div className={styles.academicsCard}>
                <div className={styles.academicsBody}>
                    {SOCIAL_PLATFORMS.map((platform) => (
                        <div
                            key={platform.key}
                            className={styles.socialRow}
                            data-testid={`${TESTID_PREFIX}.row-${platform.key}`}
                        >
                            <span className={styles.socialIconWrapper}>
                                <Img
                                    src={platform.icon}
                                    alt={platform.label}
                                    className={styles.socialIcon}
                                />
                            </span>
                            <span className={styles.socialValue}>
                                {renderHandleValue(platform)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {isEditing && isOwnProfile && hasAnnualPower && (
                <SocialMediaModal
                    currentHandles={Object.fromEntries(
                        SOCIAL_PLATFORMS.map((platform) => [platform.key, handles[platform.key].handle || '']),
                    )}
                    onClose={() => setIsEditing(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default CollegeSocial;
